import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { GraduationCap, EnvelopeSimple, ArrowLeft } from "@phosphor-icons/react";
import LanguageSwitcher from "@/components/LanguageSwitcher";

export default function ForgotPassword() {
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return toast.error(t("auth.forgot.errors.emailRequired"));
    setBusy(true);
    try {
      await api.post("/auth/forgot-password", { email: email.trim().toLowerCase() });
      setSent(true);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen w-full flex items-center justify-center bg-background p-6 relative" data-testid="forgot-page">
      <div className="absolute top-4 right-4"><LanguageSwitcher /></div>
      <div className="w-full max-w-md space-y-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-md bg-primary text-primary-foreground flex items-center justify-center">
            <GraduationCap size={22} weight="duotone" />
          </div>
          <div>
            <div className="font-bold text-lg" style={{ fontFamily: "Manrope" }}>Bilinguist</div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">School OS</div>
          </div>
        </div>

        {!sent ? (
          <>
            <div>
              <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">{t("auth.forgot.header")}</div>
              <h2 className="text-3xl font-bold mt-1" style={{ fontFamily: "Manrope" }}>{t("auth.forgot.title")}</h2>
              <p className="text-sm text-muted-foreground mt-2">{t("auth.forgot.subtitle")}</p>
            </div>
            <form onSubmit={submit} className="space-y-4">
              <div>
                <Label htmlFor="email" className="text-xs font-semibold uppercase tracking-wider">{t("auth.email")}</Label>
                <Input id="email" data-testid="forgot-email" type="email" required
                  value={email} onChange={(e) => setEmail(e.target.value)}
                  placeholder={t("auth.emailPlaceholder")} className="mt-1.5 h-11" />
              </div>
              <Button data-testid="forgot-submit" type="submit" disabled={busy} className="w-full h-11 rounded-full font-semibold">
                {busy ? t("common.sending") : t("auth.forgot.sendBtn")}
              </Button>
              <Link to="/login" data-testid="back-to-login" className="flex items-center justify-center gap-1 text-xs text-muted-foreground hover:text-primary">
                <ArrowLeft size={12} /> {t("auth.forgot.backToLogin")}
              </Link>
            </form>
          </>
        ) : (
          <div className="border border-emerald-500/40 bg-emerald-500/5 p-6 rounded-lg text-center space-y-3" data-testid="forgot-sent">
            <div className="w-14 h-14 rounded-full bg-emerald-500/15 text-emerald-600 flex items-center justify-center mx-auto">
              <EnvelopeSimple size={28} weight="duotone" />
            </div>
            <h3 className="font-bold text-lg" style={{ fontFamily: "Manrope" }}>{t("auth.forgot.sentTitle")}</h3>
            <p className="text-sm text-muted-foreground">{t("auth.forgot.sentBody", { email })}</p>
            <p className="text-[11px] text-muted-foreground bg-background/60 rounded p-2 border border-dashed border-border">
              {t("auth.forgot.devNote")}
            </p>
            <Link to="/login" className="inline-flex items-center gap-1 text-xs font-semibold text-primary hover:underline">
              <ArrowLeft size={12} /> {t("auth.forgot.backToLogin")}
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
